// Plate.js Editor Configuration - Simplified version
// Uses direct Slate operations instead of Plate plugins for better compatibility

// Custom element types for our report blocks
export const CUSTOM_ELEMENT_TYPES = {
  CHART: 'chart',
  DATA_TABLE: 'dataTable',
  HERO: 'hero',
  DIVIDER: 'divider',
  STATIC_IMAGE: 'staticImage',
  COLUMN_GROUP: 'column_group',
  COLUMN: 'column',
};

// Default editor value
export const getDefaultValue = () => [
  {
    type: 'p',
    children: [{ text: '' }],
  },
];

// Convert old block format to Plate/Slate format
export const convertBlocksToPlateValue = (blocks) => {
  if (!blocks || blocks.length === 0) {
    return getDefaultValue();
  }

  const plateNodes = [];

  blocks.forEach((block) => {
    switch (block.type) {
      case 'text':
        // Convert HTML content to Plate nodes
        const textNodes = htmlToPlateNodes(block.content?.html || '');
        plateNodes.push(...textNodes);
        break;
        
      case 'chart':
        plateNodes.push({
          type: CUSTOM_ELEMENT_TYPES.CHART,
          id: block.id,
          chartType: block.content?.chartType,
          title: block.content?.title,
          height: block.settings?.height || 300,
          reportType: block.content?.reportType,
          children: [{ text: '' }],
        });
        break;
        
      case 'table':
        plateNodes.push({
          type: CUSTOM_ELEMENT_TYPES.DATA_TABLE,
          id: block.id,
          mode: block.content?.mode,
          title: block.content?.title,
          tableData: block.content?.tableData,
          children: [{ text: '' }],
        });
        break;
        
      case 'hero':
        plateNodes.push({
          type: CUSTOM_ELEMENT_TYPES.HERO,
          id: block.id,
          title: block.content?.title,
          subtitle: block.content?.subtitle,
          backgroundImage: block.content?.backgroundImage,
          customBackgroundUrl: block.content?.customBackgroundUrl || '', // For uploaded images
          overlayColor: block.content?.overlayColor,
          overlayOpacity: block.content?.overlayOpacity,
          // Text styling
          titleColor: block.content?.titleColor || '#ffffff',
          titleSize: block.content?.titleSize || '4xl',
          titleBold: block.content?.titleBold !== undefined ? block.content.titleBold : true,
          subtitleColor: block.content?.subtitleColor || '#ffffff',
          subtitleSize: block.content?.subtitleSize || 'lg',
          subtitleOpacity: block.content?.subtitleOpacity !== undefined ? block.content.subtitleOpacity : 0.9,
          children: [{ text: '' }],
        });
        break;
        
      case 'divider':
        plateNodes.push({
          type: CUSTOM_ELEMENT_TYPES.DIVIDER,
          id: block.id,
          style: block.settings?.style,
          thickness: block.settings?.thickness,
          color: block.settings?.color,
          spacing: block.settings?.spacing,
          children: [{ text: '' }],
        });
        break;
        
      case 'image':
        plateNodes.push({
          type: CUSTOM_ELEMENT_TYPES.STATIC_IMAGE,
          id: block.id,
          src: block.content?.src,
          alt: block.content?.alt,
          children: [{ text: '' }],
        });
        break;
        
      case 'columns':
        const columns = block.content?.columns || [];
        plateNodes.push({
          type: 'column_group',
          id: block.id,
          preset: block.content?.preset,
          gap: block.settings?.gap || 16,
          children: columns.map((col) => ({
            type: 'column',
            id: col.id,
            width: `${col.width}%`,
            children: col.blocks && col.blocks.length > 0
              ? convertBlocksToPlateValue(col.blocks)
              : [{ type: 'p', children: [{ text: '' }] }],
          })),
        });
        break;
        
      default:
        // Unknown block type - treat as paragraph
        plateNodes.push({
          type: 'p',
          children: [{ text: '' }],
        });
    }
  });

  return plateNodes.length > 0 ? plateNodes : getDefaultValue();
};

// Convert Plate/Slate value back to blocks format
export const convertPlateValueToBlocks = (plateValue) => {
  if (!plateValue || plateValue.length === 0) {
    return [];
  }

  const blocks = [];
  let currentTextNodes = [];

  const flushTextNodes = () => {
    if (currentTextNodes.length > 0) {
      const html = plateNodesToHtml(currentTextNodes);
      blocks.push({
        id: `text-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'text',
        content: { html },
      });
      currentTextNodes = [];
    }
  };

  plateValue.forEach((node) => {
    switch (node.type) {
      case CUSTOM_ELEMENT_TYPES.CHART:
        flushTextNodes();
        blocks.push({
          id: node.id || `chart-${Date.now()}`,
          type: 'chart',
          content: {
            chartType: node.chartType,
            title: node.title,
            reportType: node.reportType,
          },
          settings: {
            height: node.height || 300,
          },
        });
        break;
        
      case CUSTOM_ELEMENT_TYPES.DATA_TABLE:
        flushTextNodes();
        blocks.push({
          id: node.id || `table-${Date.now()}`,
          type: 'table',
          content: {
            mode: node.mode,
            title: node.title,
            tableData: node.tableData,
          },
        });
        break;
        
      case CUSTOM_ELEMENT_TYPES.HERO:
        flushTextNodes();
        blocks.push({
          id: node.id || `hero-${Date.now()}`,
          type: 'hero',
          content: {
            title: node.title,
            subtitle: node.subtitle,
            backgroundImage: node.backgroundImage,
            customBackgroundUrl: node.customBackgroundUrl, // For uploaded images
            overlayColor: node.overlayColor,
            overlayOpacity: node.overlayOpacity,
            // Text styling
            titleColor: node.titleColor,
            titleSize: node.titleSize,
            titleBold: node.titleBold,
            subtitleColor: node.subtitleColor,
            subtitleSize: node.subtitleSize,
            subtitleOpacity: node.subtitleOpacity,
          },
        });
        break;
        
      case CUSTOM_ELEMENT_TYPES.DIVIDER:
        flushTextNodes();
        blocks.push({
          id: node.id || `divider-${Date.now()}`,
          type: 'divider',
          settings: {
            style: node.style || 'solid',
            thickness: node.thickness || 1,
            color: node.color || '#e5e7eb',
            spacing: node.spacing || 16,
          },
        });
        break;
        
      case CUSTOM_ELEMENT_TYPES.STATIC_IMAGE:
        flushTextNodes();
        blocks.push({
          id: node.id || `image-${Date.now()}`,
          type: 'image',
          content: {
            src: node.src,
            alt: node.alt,
          },
        });
        break;
        
      case 'column_group':
        flushTextNodes();
        const columns = (node.children || []).map((col) => ({
          id: col.id || `col-${Date.now()}`,
          width: parseFloat(col.width) || 50,
          blocks: convertPlateValueToBlocks(col.children || []),
        }));
        blocks.push({
          id: node.id || `columns-${Date.now()}`,
          type: 'columns',
          content: {
            preset: node.preset,
            columns,
          },
          settings: {
            gap: node.gap || 16,
          },
        });
        break;
        
      default:
        // Text nodes (paragraphs, headings, lists, etc.)
        currentTextNodes.push(node);
    }
  });

  flushTextNodes();
  return blocks;
};

// Simple HTML to Plate/Slate nodes converter
function htmlToPlateNodes(html) {
  if (!html || html === '<p></p>' || html === '<p><br></p>') {
    return [{ type: 'p', children: [{ text: '' }] }];
  }

  // Create a temporary DOM element to parse HTML
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const body = doc.body;

  const nodes = [];
  
  body.childNodes.forEach((node) => {
    const plateNode = domNodeToPlate(node);
    if (plateNode) {
      if (Array.isArray(plateNode)) {
        nodes.push(...plateNode);
      } else {
        nodes.push(plateNode);
      }
    }
  });

  return nodes.length > 0 ? nodes : [{ type: 'p', children: [{ text: '' }] }];
}

// Convert DOM node to Plate/Slate node
function domNodeToPlate(node, parentTag = null) {
  if (node.nodeType === Node.TEXT_NODE) {
    const text = node.textContent || '';
    // Skip whitespace-only text nodes
    if (!text.trim()) return null;
    return { text };
  }

  if (node.nodeType !== Node.ELEMENT_NODE) {
    return null;
  }

  const tagName = node.tagName.toLowerCase();
  
  // Skip decorative elements (like circle divs)
  const className = node.getAttribute('class') || '';
  if (className.includes('circle') || className.includes('list-item-marker')) {
    return null;
  }

  const children = Array.from(node.childNodes)
    .map(child => domNodeToPlate(child, tagName))
    .filter(Boolean)
    .flat();

  // Extract inline styles
  const style = node.getAttribute('style') || '';
  const marks = {};
  
  if (style.includes('font-weight: bold') || style.includes('font-weight:bold')) {
    marks.bold = true;
  }
  if (style.includes('color:')) {
    const colorMatch = style.match(/color:\s*([^;]+)/);
    if (colorMatch) marks.color = colorMatch[1].trim();
  }
  if (style.includes('font-size:')) {
    const sizeMatch = style.match(/font-size:\s*([^;]+)/);
    if (sizeMatch) marks.fontSize = sizeMatch[1].trim();
  }

  // Apply marks to text children
  const applyMarks = (children, marks) => {
    if (Object.keys(marks).length === 0) return children;
    return children.map((child) => {
      if (child.text !== undefined) {
        return { ...child, ...marks };
      }
      if (child.children) {
        return { ...child, children: applyMarks(child.children, marks) };
      }
      return child;
    });
  };

  // Ensure children array is not empty for block elements
  const ensureChildren = (children) => {
    if (children.length === 0) {
      return [{ text: '' }];
    }
    return children;
  };

  // Extract only text/inline nodes from mixed content
  const extractInlineContent = (children) => {
    const inlineNodes = [];
    children.forEach(child => {
      if (child.text !== undefined) {
        inlineNodes.push(child);
      } else if (child.type === 'p' && child.children) {
        // Flatten paragraph content into inline nodes
        child.children.forEach(c => {
          if (c.text !== undefined) {
            inlineNodes.push(c);
          }
        });
      }
    });
    return inlineNodes.length > 0 ? inlineNodes : [{ text: '' }];
  };

  switch (tagName) {
    case 'p':
      const alignP = style.includes('text-align:') 
        ? style.match(/text-align:\s*([^;]+)/)?.[1]?.trim()
        : undefined;
      return {
        type: 'p',
        ...(alignP && { align: alignP }),
        children: applyMarks(ensureChildren(children), marks),
      };
    
    case 'div':
      // If div is inside a list item, just return its text content
      if (parentTag === 'li') {
        const textContent = extractInlineContent(children);
        return textContent.length > 0 && textContent[0].text ? textContent : null;
      }
      const alignDiv = style.includes('text-align:') 
        ? style.match(/text-align:\s*([^;]+)/)?.[1]?.trim()
        : undefined;
      return {
        type: 'p',
        ...(alignDiv && { align: alignDiv }),
        children: applyMarks(ensureChildren(children), marks),
      };
      
    case 'h1':
      return { type: 'h1', children: applyMarks(ensureChildren(children), marks) };
    case 'h2':
      return { type: 'h2', children: applyMarks(ensureChildren(children), marks) };
    case 'h3':
      return { type: 'h3', children: applyMarks(ensureChildren(children), marks) };
    case 'h4':
      return { type: 'h4', children: applyMarks(ensureChildren(children), marks) };
    case 'h5':
      return { type: 'h5', children: applyMarks(ensureChildren(children), marks) };
    case 'h6':
      return { type: 'h6', children: applyMarks(ensureChildren(children), marks) };
      
    case 'ul':
      // Filter out non-li children
      const ulChildren = children.filter(c => c.type === 'li');
      return { type: 'ul', children: ulChildren.length > 0 ? ulChildren : [{ type: 'li', children: [{ text: '' }] }] };
    case 'ol':
      // Filter out non-li children
      const olChildren = children.filter(c => c.type === 'li');
      return { type: 'ol', children: olChildren.length > 0 ? olChildren : [{ type: 'li', children: [{ text: '' }] }] };
    case 'li':
      // Check for circle indicator in original node
      let circleType = null;
      const circleDiv = node.querySelector('.circle');
      if (circleDiv) {
        const circleClasses = circleDiv.className.split(' ');
        const typeClass = circleClasses.find(c => 
          ['averse', 'minimalist', 'cautious', 'open', 'hungry'].includes(c)
        );
        if (typeClass) circleType = typeClass;
      }
      
      // Collect all text content from children, flattening any nested structures
      const liTextContent = extractInlineContent(children);
      return { 
        type: 'li', 
        ...(circleType && { circleType }),
        children: liTextContent 
      };
      
    case 'strong':
    case 'b':
      return children.map((child) => {
        if (child.text !== undefined) {
          return { ...child, bold: true, ...marks };
        }
        return child;
      });
      
    case 'em':
    case 'i':
      return children.map((child) => {
        if (child.text !== undefined) {
          return { ...child, italic: true, ...marks };
        }
        return child;
      });
      
    case 'u':
      return children.map((child) => {
        if (child.text !== undefined) {
          return { ...child, underline: true, ...marks };
        }
        return child;
      });
      
    case 's':
    case 'strike':
      return children.map((child) => {
        if (child.text !== undefined) {
          return { ...child, strikethrough: true, ...marks };
        }
        return child;
      });
      
    case 'span':
      return applyMarks(children.length > 0 ? children : [{ text: '' }], marks);
      
    case 'br':
      return { text: '\n' };
      
    default:
      // For unknown elements, just return children if any
      if (children.length > 0) {
        return children;
      }
      return null;
  }
}

// Convert Plate/Slate nodes to HTML
function plateNodesToHtml(nodes) {
  if (!nodes || nodes.length === 0) {
    return '<p></p>';
  }

  return nodes.map(nodeToHtml).join('');
}

function nodeToHtml(node) {
  if (node.text !== undefined) {
    let html = escapeHtml(node.text);
    
    if (node.bold) html = `<strong>${html}</strong>`;
    if (node.italic) html = `<em>${html}</em>`;
    if (node.underline) html = `<u>${html}</u>`;
    if (node.strikethrough) html = `<s>${html}</s>`;
    
    const styles = [];
    if (node.color) styles.push(`color: ${node.color}`);
    if (node.fontSize) styles.push(`font-size: ${node.fontSize}`);
    if (node.backgroundColor) styles.push(`background-color: ${node.backgroundColor}`);
    
    if (styles.length > 0) {
      html = `<span style="${styles.join('; ')}">${html}</span>`;
    }
    
    return html;
  }

  const children = (node.children || []).map(nodeToHtml).join('');
  const alignStyle = node.align ? ` style="text-align: ${node.align}"` : '';

  switch (node.type) {
    case 'p':
      return `<p${alignStyle}>${children || '<br>'}</p>`;
    case 'h1':
      return `<h1${alignStyle}>${children}</h1>`;
    case 'h2':
      return `<h2${alignStyle}>${children}</h2>`;
    case 'h3':
      return `<h3${alignStyle}>${children}</h3>`;
    case 'h4':
      return `<h4${alignStyle}>${children}</h4>`;
    case 'h5':
      return `<h5${alignStyle}>${children}</h5>`;
    case 'h6':
      return `<h6${alignStyle}>${children}</h6>`;
    case 'ul':
      return `<ul>${children}</ul>`;
    case 'ol':
      return `<ol>${children}</ol>`;
    case 'li':
      return `<li>${children}</li>`;
    default:
      return children;
  }
}

function escapeHtml(text) {
  const map = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#039;',
  };
  return text.replace(/[&<>"']/g, (m) => map[m]);
}

export default {
  getDefaultValue,
  convertBlocksToPlateValue,
  convertPlateValueToBlocks,
  CUSTOM_ELEMENT_TYPES,
};
